/**
 * marginFact viewmodel — the copy and the column keys the Margin surface reads, in ONE
 * place so the trend and the drill can never end up describing different work. The order
 * book keeps HALF_COPY for the same reason; this is its equivalent for a fact with no halves.
 *
 * Pure data, no fetch (CD-7).
 */

import { periodHeading } from '../components/kit/cellFormat'

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/

/** A scope value that is a real YYYY-MM, or null.
 *
 * The margin routes window on the invoice month, so a malformed scope must be NO window rather
 * than a claim we applied one. It is shared rather than re-written per caller because the page
 * and the explorer must produce the SAME useMarginTimeline query key — one cached payload, and
 * two panels that cannot end up reporting different months. */
export function monthOrNull(period: string | null): string | null {
  return period !== null && MONTH_RE.test(period) ? period : null
}

/** '2026-08' -> '2026-07'. January steps back a year. */
function previousMonth(month: string): string {
  const [year, index] = month.split('-').map(Number)
  return index === 1
    ? `${year - 1}-12`
    : `${year}-${String(index - 1).padStart(2, '0')}`
}

/**
 * The window a card should NAME, as the platform labels months — 'Jan 26 → Jul 26'.
 *
 * ⚠ THE ASKED-FOR WINDOW IS NOT THE SERVED ONE. The server serves closed months only, so a
 * scope of 2026 read on 23 Aug is answered with January to July. A card headed
 * 'Jan 26 → Dec 26' above a chart that stops at July is the same mismatch the closed-period
 * rule exists to remove, one level up — so the excluded months clamp the label too.
 *
 * The clamp takes the EARLIEST excluded month and names the month before it. That is right
 * for a future-dated row as well as for the open one: ['2026-08', '2030-01'] serves through
 * July 2026, because everything from August is unclosed whatever its date claims.
 */
export function servedSpan(
  from: string | null,
  to: string | null,
  openMonths: readonly string[] = []
): string | null {
  if (from === null || to === null) return from === null ? null : periodHeading(from)
  const earliestOpen = openMonths.length ? openMonths.reduce((a, b) => (a < b ? a : b)) : null
  const last = earliestOpen === null ? to : previousMonth(earliestOpen)
  const end = last < to ? last : to
  if (end < from) return null // the whole window is open — there is nothing to name
  return end === from ? periodHeading(from) : `${periodHeading(from)} → ${periodHeading(end)}`
}

/** The month column the backend labels its invoice-month rollup with (margin_line_shape
 * LEVEL_LABEL). NOT "Due month" — that is the order book's demand date, and confusing the
 * two is the defect this whole fact exists to fix. */
export const MONTH_KEY = 'Invoice month'

export const MARGIN_COPY = {
  calendar: 'Margin earned by invoice month',
  /** The line under the cards. It says the POPULATION and nothing else.
   *
   * ⚠ IT NAMED BOTH ENTITIES UNTIL 24 Aug 2026, as static copy — so standing on Kirkstall you
   * were told about Oracle's quote basis, on a page reporting no Oracle. The basis is already
   * stated TWICE above it, by the coverage band and the Cost card, and both derive it from the
   * served lenses so they follow the scope. Static copy cannot, which is the whole lesson. */
  note: 'billed lines',
  empty: 'No invoiced lines for',
  /** ⚠ Says nothing about the cost BASIS — the ring beside this chart names it from the served
   * lenses, and the coverage band above states it per entity. Naming entities here made the
   * caption wrong at every single-entity scope. */
  caption:
    'Every month here is CLOSED, and holds work that has been billed, periodised on the invoice date — the basis that reconciles to Sage. The month being billed right now is not shown: it is still taking invoices and still taking cost, so it is not a margin yet.',
} as const
